import snakeCase from 'lodash/snakeCase'
import { useMemo } from 'react'
import { OPTIONS_KEY_BUNDLE, OPTIONS_KEY_CONFIGURABLE } from '../constants'
import { useCurrentProductQueryParams } from './useCurrentProductQueryParams'

export const useProductUrlParams = ({ product = {} }) => {
	const configurableOptions = useMemo(() => product.configurableOptions ?? [], [product])
	const bundleOptions = useMemo(() => product.bundleOptions ?? [], [product])
	const currentProductConfigurableOptionsQuery = useCurrentProductQueryParams({ configurableOptions })
	const currentProductBundleOptionsQuery = useCurrentProductQueryParams({ bundleOptions })

	const formConfigurableDefaultValues = useMemo(
		() =>
			currentProductConfigurableOptionsQuery
				? Object.entries(currentProductConfigurableOptionsQuery).reduce((acc, [code, valueLabel]) => {
						const option = configurableOptions.find(
							({ attributeCode }) => attributeCode.toLowerCase() === code.toLowerCase()
						)
						const optionValue = option?.values.find(
							({ label }) => label.toLowerCase() === valueLabel.toLowerCase()
						)?.value

						return {
							...acc,
							[OPTIONS_KEY_CONFIGURABLE]: {
								...acc[OPTIONS_KEY_CONFIGURABLE],
								...(option ? { [`_${option.attributeId}`]: optionValue } : {}),
							},
						}
				  }, {})
				: null,
		[currentProductConfigurableOptionsQuery, configurableOptions]
	)

	const formBundleDefaultValues = useMemo(
		() =>
			currentProductBundleOptionsQuery
				? Object.entries(currentProductBundleOptionsQuery).reduce((acc, [code, valueLabels]) => {
						const bundleOption = bundleOptions.items.find(({ label }) => snakeCase(label?.toLowerCase?.()) === code)
						if (!bundleOption) return acc

						const { type } = bundleOption
						const optionValues = bundleOption?.children.filter(({ productSku }) => valueLabels.includes(productSku))

						let selectionsValue
						let qtyValue

						// keep same logic as in packages/ui-logic/src/providers/product/form-schemas/bundle/get-bundle-options-form-default-value
						switch (type) {
							case 'radio':
							case 'select':
								selectionsValue = optionValues?.[0]?.id?.toString?.()
								qtyValue = optionValues?.[0]?.defaultQty
								break
							case 'checkbox':
							case 'multi':
								selectionsValue = optionValues.reduce((acc, { id }) => ({ ...acc, [`_${id}`]: true }), {})
								qtyValue = optionValues.reduce((acc, { id, defaultQty }) => ({ ...acc, [`_${id}`]: defaultQty }), {})
								break
						}

						return {
							...acc,
							[OPTIONS_KEY_BUNDLE]: {
								...acc[OPTIONS_KEY_BUNDLE],
								...{ [`_${bundleOption.id}`]: { selections: selectionsValue, qty: qtyValue } },
							},
						}
				  }, {})
				: null,
		[currentProductBundleOptionsQuery, bundleOptions]
	)

	const formDefaultValues = useMemo(() => {
		return {
			...(formConfigurableDefaultValues ?? {}),
			...(formBundleDefaultValues ?? {}),
		}
	}, [formConfigurableDefaultValues, formBundleDefaultValues])

	return formDefaultValues
}
